import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Filter, ExternalLink, ChevronDown, Map, List, Star } from "lucide-react";
import "leaflet/dist/leaflet.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FeaturedVenues({ venues }) {
  if (!venues || venues.length === 0) return null;
  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="featured-venues">
      <h2 className="flex items-center gap-2 text-lg font-heading font-black text-brand-navy mb-4">
        <Star size={18} className="text-brand-orange" fill="currentColor" /> Featured Venues
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {venues.map((v) => (
          <div key={v.id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid="featured-venue-card">
            {v.has_photo && (
              <img src={`${API}/facility/photo/${v.id}`} alt={v.name} className="w-full h-40 object-cover" />
            )}
            <div className="p-5">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-bold text-brand-navy">{v.name}</h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-brand-orange/10 text-brand-orange border border-brand-orange/20 whitespace-nowrap">Partner</span>
              </div>
              <p className="flex items-center gap-1 text-xs text-gray-500 mb-3"><MapPin size={11} /> {v.address || v.city}</p>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {(v.sport || []).map((s) => <span key={s} className="text-xs px-2 py-0.5 rounded bg-brand-green/10 text-brand-green capitalize">{s}</span>)}
              </div>
              {(v.booking_url || v.website) ? (
                <a href={v.booking_url || v.website} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-green text-brand-navy text-sm font-bold rounded-xl hover:bg-brand-green/90 transition-colors"
                  data-testid="featured-book-now-btn">
                  Book Now <ExternalLink size={13} />
                </a>
              ) : (
                <span className="text-xs text-gray-600">Booking info at venue</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const SPORTS = ["tennis", "pickleball", "golf", "table-tennis", "ski", "padel", "squash", "badminton", "climbing"];
const SPORT_LABELS = {
  "tennis": "Tennis", "pickleball": "Pickleball", "golf": "Golf", "table-tennis": "Table Tennis",
  "ski": "Skiing", "padel": "Padel", "squash": "Squash", "badminton": "Badminton", "climbing": "Climbing"
};

function VenueCard({ venue }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 hover:border-brand-green/30 transition-all" data-testid="venue-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-bold text-brand-navy text-sm mb-1">{venue.name}</h3>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <MapPin size={10} /> {venue.address || `${venue.city}, ${venue.state}`}
          </div>
        </div>
        {/* Badge text must clear 4.5:1 on its own tinted background:
            text-green-400 (#4ade80) on green-500/15 measured 1.53:1. */}
        <span className={`text-xs px-2 py-1 rounded flex-shrink-0 font-medium ${
          venue.venue_type === "public" ? "bg-green-500/15 text-green-800" : "bg-blue-500/15 text-blue-800"
        }`}>
          {venue.venue_type}
        </span>
      </div>

      {/* Sports tags */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {(venue.sports || []).map((s) => (
          <span key={s} className="text-xs px-2 py-0.5 rounded bg-brand-green/10 text-brand-green">{SPORT_LABELS[s] || s}</span>
        ))}
      </div>

      {/* Details */}
      <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 mb-3">
        <div><span className="text-slate-600">Surface:</span> {venue.surface || "N/A"}</div>
        <div><span className="text-slate-600">Indoor:</span> {venue.indoor ? "Yes" : "Outdoor"}</div>
        <div><span className="text-slate-600">Lights:</span> {venue.lights ? "Yes" : "No"}</div>
        <div><span className="text-slate-600">Fee:</span> {venue.fee || "Check venue"}</div>
        {venue.courts && <div><span className="text-slate-600">Courts:</span> {venue.courts}</div>}
        <div><span className="text-slate-600">Booking:</span> {venue.reservation_required ? "Required" : "Walk-in"}</div>
      </div>

      {venue.description && (
        <p className="text-xs text-gray-600 mb-3">{venue.description}</p>
      )}

      {/* Amenities */}
      {venue.amenities?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {venue.amenities.slice(0, 3).map((a) => (
            <span key={a} className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{a}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// Lazy-loaded map component
function VenueMap({ venues }) {
  const [MapComponent, setMapComponent] = useState(null);

  useEffect(() => {
    // Dynamic import to avoid SSR issues
    Promise.all([
      import("react-leaflet"),
      import("leaflet"),
    ]).then(([reactLeaflet, L]) => {
      // Fix default marker icons
      delete L.default.Icon.Default.prototype._getIconUrl;
      L.default.Icon.Default.mergeOptions({
        iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
        iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
        shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
      });
      setMapComponent({ ...reactLeaflet.default || reactLeaflet, L: L.default || L });
    });
  }, []);

  if (!MapComponent) return (
    <div className="h-full flex items-center justify-center bg-gray-100">
      <div className="w-6 h-6 border-2 border-brand-green border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const { MapContainer, TileLayer, Marker, Popup } = MapComponent;
  const L = MapComponent.L;

  // Leaflet's default marker is a bare <img alt="Marker"> with role="button" and
  // tabindex=0, so ten focusable markers all announce identically as "Marker".
  // Passing `keyboard={{ title }}` to react-leaflet does not help: the prop never
  // reaches Leaflet's Marker options, so no title is written onto the element.
  // A divIcon puts the venue name in the marker's *content*, which is the
  // highest-priority source for an accessible name, and hides the decorative
  // pin image from assistive technology.
  const markerIcon = (venue) =>
    L.divIcon({
      className: "venue-marker",
      html:
        '<img src="https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png" alt="" aria-hidden="true" ' +
        'style="width:25px;height:41px;display:block">' +
        `<span class="sr-only">${venue.name}, ${venue.city}</span>`,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -36],
    });

  // A venue's coordinates are deliberately optional. The Blok Climbing Co has a
  // verified address but no verified map position, and inventing coordinates to
  // fill a gap would send someone to the wrong place - so lat/lng are null.
  //
  // Both the centre and the markers therefore have to tolerate that. Averaging
  // null yields null and Leaflet rejects it with "Invalid LatLng object", which
  // took the whole venues page down. Only venues with real coordinates may be
  // placed on the map or used to centre it.
  const mappable = venues.filter(
    (v) => typeof v.lat === "number" && typeof v.lng === "number"
      && !Number.isNaN(v.lat) && !Number.isNaN(v.lng)
  );

  const center = mappable.length > 0
    ? [
        mappable.reduce((s, v) => s + v.lat, 0) / mappable.length,
        mappable.reduce((s, v) => s + v.lng, 0) / mappable.length,
      ]
    : [32.755, -97.33]; // Fort Worth, used only when nothing is mappable

  return (
    <MapContainer center={center} zoom={12} className="h-full w-full" style={{ background: "#f1f5f9" }}>
      <TileLayer
        url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>'
      />
      {/* Only mappable venues get a marker; every venue is still listed below the
          map, so an unplaced venue stays discoverable rather than vanishing. */}
      {mappable.map((venue) => (
        <Marker
          key={venue.id}
          position={[venue.lat, venue.lng]}
          icon={markerIcon(venue)}
        >
          <Popup>
            <div style={{ color: "#1B2A5B", minWidth: 180 }}>
              <strong style={{ display: "block", marginBottom: 4 }}>{venue.name}</strong>
              <span style={{ fontSize: 12, color: "#6B7280" }}>{venue.city}</span>
              <br />
              <span style={{ fontSize: 12, color: "#B45309", fontWeight:600 }}>{(venue.sports || []).join(", ")}</span>
              <br />
              <span style={{ fontSize: 11, color: "#5B6478" }}>{venue.indoor ? "Indoor" : "Outdoor"} · {venue.fee || "Fee unknown"}</span>
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
}

export default function VenuesPage() {
  const [venues, setVenues] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ sport: "", city: "Fort Worth", indoor: "" });
  const [view, setView] = useState("split"); // split, list, map

  useEffect(() => { fetchVenues(); }, [filters]);
  useEffect(() => {
    axios.get(`${API}/facilities/featured`).then(({ data }) => setFeatured(data.facilities || [])).catch(() => {});
  }, []);

  const fetchVenues = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.sport) params.set("sport", filters.sport);
      if (filters.city) params.set("city", filters.city);
      if (filters.indoor === "true") params.set("indoor", "true");
      if (filters.indoor === "false") params.set("indoor", "false");
      const { data } = await axios.get(`${API}/venues?${params}`);
      setVenues(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 py-6 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-black text-brand-navy">Venue Directory</h1>
            <p className="text-gray-500 text-sm">{venues.length} venues found</p>
          </div>
          <div className="flex gap-2">
            {["split", "list", "map"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors capitalize ${view === v ? "bg-brand-orange text-white border-brand-green font-bold" : "border-gray-200 text-gray-500 hover:border-brand-green/40"}`}
              >
                {v === "list" ? <List size={12} /> : v === "map" ? <Map size={12} /> : <><Map size={12} /><List size={12} /></>}
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="max-w-7xl mx-auto mt-4 flex flex-col sm:flex-row gap-3">
          <div className="relative">
            <select
              id="venue-sport-filter"
              aria-label="Filter venues by sport"
              value={filters.sport}
              onChange={(e) => setFilters({ ...filters, sport: e.target.value })}
              className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 text-brand-navy text-sm appearance-none pr-8 focus:outline-none focus:ring-2 focus:ring-brand-green/40 cursor-pointer"
              data-testid="venue-sport-filter"
            >
              <option value="">All Sports</option>
              {SPORTS.map((s) => <option key={s} value={s}>{SPORT_LABELS[s]}</option>)}
            </select>
            <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
          </div>
          <div className="relative">
            <MapPin size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
            <input
              type="text"
              aria-label="Filter venues by city"
              value={filters.city}
              onChange={(e) => setFilters({ ...filters, city: e.target.value })}
              placeholder="City..."
              className="bg-gray-50 border border-gray-200 rounded-xl pl-8 pr-4 py-2 text-brand-navy text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-green/40"
              data-testid="venue-city-filter"
            />
          </div>
          <div className="relative">
            <select
              aria-label="Filter by indoor or outdoor"
              value={filters.indoor}
              onChange={(e) => setFilters({ ...filters, indoor: e.target.value })}
              className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 text-brand-navy text-sm appearance-none pr-8 focus:outline-none focus:ring-2 focus:ring-brand-green/40 cursor-pointer"
              data-testid="venue-indoor-filter"
            >
              <option value="">Indoor & Outdoor</option>
              <option value="true">Indoor Only</option>
              <option value="false">Outdoor Only</option>
            </select>
            <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Featured partner venues */}
      {view !== "map" && <FeaturedVenues venues={featured} />}

      {/* The results region has no visible heading, so in map view the venue
          <h3>s followed the page <h1> directly - an h1 to h3 jump that breaks
          the heading outline for screen-reader users. Adding a visually hidden
          <h2> makes the order h1 > h2 > h3 unconditionally, whichever view is
          active and whether FeaturedVenues rendered an <h2> of its own. */}
      <h2 className="sr-only">
        {view === "map" ? "Venues on the map" : "All venues"}
      </h2>

      {/* Content */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-brand-green border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="flex-1 overflow-hidden" data-testid="venues-content">
          {view === "map" ? (
            <div className="h-[calc(100vh-200px)]">
              <VenueMap venues={venues} />
            </div>
          ) : view === "list" ? (
            <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="venues-list">
              {venues.map((v) => <VenueCard key={v.id} venue={v} />)}
              {venues.length === 0 && (
                <div className="col-span-full text-center py-16 text-gray-600">No venues found. Try adjusting filters.</div>
              )}
            </div>
          ) : (
            /* Split view */
            <div className="flex h-[calc(100vh-220px)]">
              {/* tabIndex makes this scroll container reachable by keyboard (2.1.1);
                  axe flags scrollable regions with no focusable content. */}
              <div
                className="w-1/2 overflow-y-auto p-4 grid gap-4 content-start"
                tabIndex={0}
                role="region"
                aria-label="Venue results"
              >
                {venues.map((v) => <VenueCard key={v.id} venue={v} />)}
                {venues.length === 0 && (
                  <div className="text-center py-16 text-gray-600">No venues found.</div>
                )}
              </div>
              <div className="w-1/2 border-l border-[#2A3143] dark-section">
                <VenueMap venues={venues} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
